import Hls from "hls.js";
import { ChevronLeft, ChevronRight, Radio, ScreenShare, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "./api";
import { useAuth } from "./auth";
import { isDesktopApp, type DesktopCaptureSource } from "./desktop";
import { useRealtime } from "./realtime";
import type { LiveStream, RealtimePayload } from "./types";
import { IconButton } from "./ui";
import { DetachedLiveViewerWindow } from "./liveWindows";
import { nextAutoQualityIndex, publisherReconnectDelay, selectedVideoAvailableOutgoingBitrate } from "./liveNetworkQuality";

type LiveScope = { scopeType: "dm" | "channel"; scopeId: string; label?: string };
type QualityMode = "auto" | "480p" | "720p";
type QualityProfile = { width: number; height: number; fps: number; maxBitrate: number; label: string };
type ShareConfig = { source: DesktopCaptureSource; quality: QualityMode; includeComputerAudio: boolean; computerAudioVolume: number; includeMicrophone: boolean; microphoneVolume: number };
type CaptureSession = { stream: MediaStream; sourceStreams: MediaStream[]; audioContext?: AudioContext; computerGain?: GainNode; microphoneGain?: GainNode };
export type MediaGrant = { path: string; token: string; expiresAt: string; hlsUrl?: string | null; whepUrl?: string | null; whipUrl?: string | null };
export type WatchResponse = { stream: LiveStream; media: MediaGrant | null };

const AUTO_LADDER: QualityProfile[] = [
  { width: 640, height: 360, fps: 15, maxBitrate: 400_000, label: "360p" },
  { width: 854, height: 480, fps: 20, maxBitrate: 700_000, label: "480p" },
  { width: 960, height: 540, fps: 24, maxBitrate: 1_000_000, label: "540p" },
  { width: 1280, height: 720, fps: 24, maxBitrate: 1_500_000, label: "720p" },
  { width: 1280, height: 720, fps: 30, maxBitrate: 2_000_000, label: "720p" },
];

type VoiceRtcConfig = { stunUrl?: string; turnUrl?: string; turnUrls?: string[]; turnUsername?: string; turnCredential?: string };

async function liveRtcConfiguration(signal?: AbortSignal): Promise<RTCConfiguration> {
  const controller = new AbortController();
  const abortRequest = () => controller.abort();
  if (signal?.aborted) abortRequest();
  else signal?.addEventListener("abort", abortRequest, { once: true });
  const timeout = window.setTimeout(() => controller.abort(), 4_000);
  try {
    const config = await api<VoiceRtcConfig>("/voice/config", { signal: controller.signal });
    const iceServers: RTCIceServer[] = [];
    if (config.stunUrl) iceServers.push({ urls: config.stunUrl });
    const turnUrls = config.turnUrls?.length ? config.turnUrls : config.turnUrl ? [config.turnUrl] : [];
    if (turnUrls.length && config.turnUsername && config.turnCredential) {
      iceServers.push({ urls: turnUrls, username: config.turnUsername, credential: config.turnCredential });
    }
    return { iceServers };
  } catch (error) {
    if (signal?.aborted) throw error;
    // Preserve direct ICE as a fallback if the optional ICE configuration call
    // is temporarily unavailable; publisher recovery will still retry later.
    window.verseDesktop?.logScreenShareStage({ stage: "live-ice-config-failed", detail: error instanceof Error ? error.message : String(error) });
    return { iceServers: [] };
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener("abort", abortRequest);
  }
}

function streamId() { return globalThis.crypto?.randomUUID?.() || `live-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
function profileFor(mode: QualityMode, autoIndex: number) { return mode === "480p" ? AUTO_LADDER[1] : mode === "720p" ? AUTO_LADDER[4] : AUTO_LADDER[Math.max(0, Math.min(AUTO_LADDER.length - 1, autoIndex))]; }
function safeSourceLabel(source: DesktopCaptureSource) { const value = String(source.name || "").toLowerCase(); if (value.includes("star citizen")) return "Star Citizen"; if (value.includes("screen")) return "Screen"; if (value.includes("chrome") || value.includes("edge") || value.includes("firefox")) return "Browser"; if (value.includes("visual studio") || value.includes("code")) return "Code editor"; return "Desktop window"; }
function shareAudioLabel(config: ShareConfig | null) { if (!config) return "Audio off"; const voiceOwnsMic = Boolean((window as Window & { __verseVoiceMicActive?: boolean }).__verseVoiceMicActive); const active = [config.includeComputerAudio ? `Computer ${config.computerAudioVolume}%` : "", config.includeMicrophone && !voiceOwnsMic ? `Mic ${config.microphoneVolume}%` : voiceOwnsMic && config.includeMicrophone ? "Mic via call" : ""].filter(Boolean); return active.length ? active.join(" · ") : "Audio off"; }

async function waitForIceGathering(peer: RTCPeerConnection) {
  if (peer.iceGatheringState === "complete") return;
  await new Promise<void>(resolve => { let timeout = 0; const done = () => { peer.removeEventListener("icegatheringstatechange", onChange); window.clearTimeout(timeout); resolve(); }; const onChange = () => { if (peer.iceGatheringState === "complete") done(); }; peer.addEventListener("icegatheringstatechange", onChange); timeout = window.setTimeout(done, 5_000); });
}
async function configureSender(sender: RTCRtpSender, profile: QualityProfile, track: MediaStreamTrack) {
  try { const parameters = sender.getParameters(); const encoding = parameters.encodings?.[0] || {}; parameters.encodings = [{ ...encoding, maxBitrate: profile.maxBitrate, maxFramerate: profile.fps }]; await sender.setParameters(parameters); } catch { /* optional Chromium fields */ }
  try { const settings = track.getSettings(); const scale = Math.max(Number(settings.width || profile.width) / profile.width, Number(settings.height || profile.height) / profile.height, 1); const parameters = sender.getParameters(); const encoding = parameters.encodings?.[0] || {}; parameters.encodings = [{ ...encoding, scaleResolutionDownBy: scale, maxBitrate: profile.maxBitrate, maxFramerate: profile.fps }]; await sender.setParameters(parameters); } catch { /* optional Chromium fields */ }
}
type WhipPublisher = { peer: RTCPeerConnection; sender: RTCRtpSender; dispose: () => void };

async function publishWhip(media: MediaGrant, stream: MediaStream, profile: QualityProfile, onStats: (available: number, lossRatio: number, encoderLimited: boolean) => void, onConnectionState: (state: string) => void, signal: AbortSignal): Promise<WhipPublisher> {
  if (!media.whipUrl) throw new Error("Live streaming is temporarily unavailable.");
  const rtcConfiguration = await liveRtcConfiguration(signal);
  if (signal.aborted) throw new DOMException("Publisher start was cancelled.", "AbortError");
  const peer = new RTCPeerConnection(rtcConfiguration);
  const publisherAuthorization = `Basic ${window.btoa(`versecomms:${media.token}`)}`;
  let sessionUrl: string | null = null;
  let profileTimer = 0;
  let connectTimer = 0;
  let reportConnectionState: (() => void) | null = null;
  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    window.clearInterval(profileTimer);
    window.clearTimeout(connectTimer);
    if (reportConnectionState) {
      peer.removeEventListener("connectionstatechange", reportConnectionState);
      peer.removeEventListener("iceconnectionstatechange", reportConnectionState);
    }
    signal.removeEventListener("abort", onAbort);
    peer.close();
    if (sessionUrl) {
      void fetch(sessionUrl, { method: "DELETE", headers: { Authorization: publisherAuthorization } }).catch(() => undefined);
      sessionUrl = null;
    }
  };
  const onAbort = () => dispose();
  signal.addEventListener("abort", onAbort, { once: true });
  try {
    if (signal.aborted) throw new DOMException("Publisher start was cancelled.", "AbortError");
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error("No screen video track is available.");
    try { track.contentHint = "motion"; } catch { /* unsupported */ }
    const transceiver = peer.addTransceiver(track, { direction: "sendonly" });
    stream.getAudioTracks().forEach(audioTrack => peer.addTransceiver(audioTrack, { direction: "sendonly" }));
    try { const h264 = RTCRtpSender.getCapabilities?.("video")?.codecs.filter(codec => codec.mimeType.toLowerCase() === "video/h264") || []; if (h264.length) transceiver.setCodecPreferences?.(h264); } catch { /* optional */ }
    await configureSender(transceiver.sender, profile, track);
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    await waitForIceGathering(peer);
    if (signal.aborted) throw new DOMException("Publisher start was cancelled.", "AbortError");
    const response = await fetch(media.whipUrl, { method: "POST", headers: { Authorization: publisherAuthorization, "Content-Type": "application/sdp", Accept: "application/sdp" }, body: peer.localDescription?.sdp || offer.sdp || "", signal });
    const responseBody = await response.text();
    if (!response.ok) {
      window.verseDesktop?.logScreenShareStage({ stage: "whip-publish-failed", detail: `HTTP ${response.status}: ${responseBody.slice(0, 240)}` });
      throw new Error(`Live publisher unavailable (${response.status}).`);
    }
    const location = response.headers.get("Location");
    if (!location) throw new Error("WHIP session URL missing.");
    sessionUrl = new URL(location, media.whipUrl).toString();
    if (signal.aborted) throw new DOMException("Publisher start was cancelled.", "AbortError");
    window.verseDesktop?.logScreenShareStage({ stage: "PUBLISH_WHIP_SUCCESS", detail: `HTTP ${response.status}` });
    await peer.setRemoteDescription({ type: "answer", sdp: responseBody });
    reportConnectionState = () => { const state = peer.connectionState === "new" ? peer.iceConnectionState : peer.connectionState; if (state === "connected" || state === "completed") window.clearTimeout(connectTimer); window.verseDesktop?.logScreenShareStage({ stage: "whip-connection-state", detail: `${state}/${peer.iceConnectionState}` }); onConnectionState(state); };
    peer.addEventListener("connectionstatechange", reportConnectionState);
    peer.addEventListener("iceconnectionstatechange", reportConnectionState);
    reportConnectionState();
    if (peer.connectionState !== "connected" && peer.connectionState !== "closed") {
      connectTimer = window.setTimeout(() => {
        if (peer.connectionState !== "connected" && peer.connectionState !== "closed") {
          window.verseDesktop?.logScreenShareStage({ stage: "publisher-connect-timeout", detail: `${peer.connectionState}/${peer.iceConnectionState}` });
          onConnectionState("failed");
        }
      }, 15_000);
    }
    const previousReceiverCounters = new Map<string, { lost: number; received: number }>();
    let lastEncoderTelemetry = "";
    profileTimer = window.setInterval(() => { void (async () => { try {
    const stats = await peer.getStats();
    let available = 0;
    let intervalLost = 0;
    let intervalPackets = 0;
    let encoderLimited = false;
    stats.forEach(report => {
      if (report.type === "outbound-rtp" && report.kind === "video") {
        const outbound = report as RTCOutboundRtpStreamStats & { encoderImplementation?: string; powerEfficientEncoder?: boolean };
        const limitation = outbound.qualityLimitationReason || "unreported";
        encoderLimited = limitation === "cpu" || limitation === "bandwidth";
        const codec = outbound.codecId ? stats.get(outbound.codecId) as { mimeType?: string } | undefined : undefined;
        const telemetry = `${codec?.mimeType || "codec-unknown"}; encoder=${outbound.encoderImplementation || "unreported"}; power-efficient=${typeof outbound.powerEfficientEncoder === "boolean" ? outbound.powerEfficientEncoder : "unreported"}; limit=${limitation}; ${outbound.frameWidth || "?"}x${outbound.frameHeight || "?"}@${outbound.framesPerSecond || "?"}fps`;
        if (telemetry !== lastEncoderTelemetry) {
          lastEncoderTelemetry = telemetry;
          window.verseDesktop?.logScreenShareStage({ stage: "encoder-stats", detail: telemetry });
        }
        return;
      }
      if (report.type !== "remote-inbound-rtp" || report.kind !== "video") return;
      const lost = Math.max(0, Number(report.packetsLost));
      const received = Number(report.packetsReceived);
      if (!Number.isFinite(lost) || !Number.isFinite(received)) return;
      const previous = previousReceiverCounters.get(report.id);
      if (previous) {
        const lostDelta = lost >= previous.lost ? lost - previous.lost : lost;
        const receivedDelta = received >= previous.received ? received - previous.received : received;
        intervalLost += lostDelta;
        intervalPackets += lostDelta + receivedDelta;
      }
      previousReceiverCounters.set(report.id, { lost, received });
    });
    available = selectedVideoAvailableOutgoingBitrate(stats.values());
    const lossRatio = intervalPackets > 0 ? intervalLost / intervalPackets : 0;
    onStats(available, lossRatio, encoderLimited);
    } catch (error) { window.verseDesktop?.logScreenShareStage({ stage: "publish-stats-warning", detail: error instanceof Error ? error.message : String(error) }); } })(); }, 5_000);
    return { peer, sender: transceiver.sender, dispose };
  } catch (error) {
    dispose();
    throw error;
  }
}

export function HlsVideo({ media, label, publisherReady }: { media: MediaGrant; label: string; publisherReady?: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null); const tokenRef = useRef(media.token); const retryWhepOnPublisherReadyRef = useRef<(() => void) | null>(null); const publisherReadyRef = useRef(Boolean(publisherReady)); const [status, setStatus] = useState("Connecting with low latency…");
  useEffect(() => { tokenRef.current = media.token; }, [media.token]);
  useEffect(() => {
    const becameReady = !publisherReadyRef.current && Boolean(publisherReady);
    publisherReadyRef.current = Boolean(publisherReady);
    if (becameReady) retryWhepOnPublisherReadyRef.current?.();
  }, [publisherReady]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let hls: Hls | null = null;
    let peer: RTCPeerConnection | null = null;
    let whepAttemptId = 0;
    let whepStarting = false;
    let whepSessionUrl: string | null = null;
    let whepRequestController: AbortController | null = null;
    let fallbackTimer = 0;
    let disconnectedTimer = 0;
    let whepRetryTimer = 0;
    let frameWatchTimer = 0;
    let hlsRetryTimer = 0;
    let hlsRetryCount = 0;
    let hlsFatalRetryCount = 0;
    let whepRetryCount = 0;
    let publisherReadyRetryConsumed = false;
    let mediaRecoveryAt = 0;
    let videoFrameCallbackId: number | null = null;
    let loadedDataListener: (() => void) | null = null;
    let lastLatencyLogAt = 0;
    let lastDecodedFrameAt = 0;
    let nextFrameRecoveryAt = 0;
    let frameRecoveryCount = 0;
    let receivedVideo = false;
    let remoteStream = new MediaStream();
    const clearVideoFrameWait = () => {
      if (videoFrameCallbackId !== null) {
        video.cancelVideoFrameCallback?.(videoFrameCallbackId);
        videoFrameCallbackId = null;
      }
      if (loadedDataListener) {
        video.removeEventListener("loadeddata", loadedDataListener);
        loadedDataListener = null;
      }
    };
    const markReceivedVideo = () => {
      if (cancelled || receivedVideo) return;
      receivedVideo = true;
      hlsRetryCount = 0;
      frameRecoveryCount = 0;
      nextFrameRecoveryAt = 0;
      if (video.dataset.liveTransport === "hls") whepRetryCount = 0;
      window.clearTimeout(hlsRetryTimer);
      hlsRetryTimer = 0;
      window.clearTimeout(fallbackTimer);
      if (video.dataset.liveTransport === "hls") scheduleWhepRetry();
      else window.clearTimeout(whepRetryTimer);
      setStatus(video.dataset.liveTransport === "hls" ? "Playing · compatibility mode" : "Playing · low latency");
    };
    const waitForVideoFrame = () => {
      clearVideoFrameWait();
      if (typeof video.requestVideoFrameCallback === "function") {
        const recordFrame = (now: number, metadata: VideoFrameCallbackMetadata) => {
          videoFrameCallbackId = null;
          lastDecodedFrameAt = Date.now();
          markReceivedVideo();
          if (typeof metadata.captureTime === "number") {
            const latencyMs = Math.max(0, Math.round(now - metadata.captureTime));
            video.dataset.liveLatencyMs = String(latencyMs);
            if (Date.now() - lastLatencyLogAt >= 5_000) {
              lastLatencyLogAt = Date.now();
              window.verseDesktop?.logScreenShareStage({ stage: "live-frame-latency", detail: `${latencyMs}ms` });
            }
          }
          if (!cancelled && receivedVideo) videoFrameCallbackId = video.requestVideoFrameCallback(recordFrame);
        };
        videoFrameCallbackId = video.requestVideoFrameCallback(recordFrame);
      } else {
        loadedDataListener = markReceivedVideo;
        video.addEventListener("loadeddata", loadedDataListener, { once: true });
      }
    };
    const stopWhep = () => {
      // Invalidate any async ICE/fetch work before closing its peer. A failed
      // gather can resolve after the next retry has already started.
      whepAttemptId += 1;
      whepStarting = false;
      clearVideoFrameWait();
      window.clearTimeout(fallbackTimer);
      whepRequestController?.abort();
      whepRequestController = null;
      if (peer) {
        peer.ontrack = null;
        peer.close();
        peer = null;
      }
      remoteStream.getTracks().forEach(track => remoteStream.removeTrack(track));
      if (whepSessionUrl) {
        void fetch(whepSessionUrl, { method: "DELETE", headers: { Authorization: `Basic ${btoa(`versecomms:${tokenRef.current}`)}` } }).catch(() => undefined);
        whepSessionUrl = null;
      }
      window.clearTimeout(disconnectedTimer);
    };
    const scheduleWhepRetry = () => {
      window.clearTimeout(whepRetryTimer);
      if (cancelled || !media.whepUrl) return;
      whepRetryTimer = window.setTimeout(() => {
        const hlsIsPlaying = video.dataset.liveTransport === "hls";
        if (cancelled || (receivedVideo && !hlsIsPlaying) || peer) return;
        whepRetryCount += 1;
        setStatus(hlsIsPlaying ? "Checking low-latency video…" : "Reconnecting with low latency…");
        void startWhep(hlsIsPlaying);
      }, video.dataset.liveTransport === "hls"
        ? (!receivedVideo || Date.now() - lastDecodedFrameAt > 2_500
          ? Math.min(6_000, 1_500 * 2 ** Math.min(whepRetryCount, 2))
          : 15_000)
        : 15_000);
    };
    const startHls = () => {
      if (cancelled) return;
      window.clearTimeout(hlsRetryTimer);
      hlsRetryTimer = 0;
      stopWhep();
      if (!media.hlsUrl) {
        receivedVideo = false;
        setStatus("Live stream unavailable. Retrying…");
        scheduleWhepRetry();
        return;
      }
      if (!hls) {
        receivedVideo = false;
        video.srcObject = null;
        video.removeAttribute("src");
        video.load();
        video.dataset.liveTransport = "hls";
        delete video.dataset.liveLatencyMs;
        setStatus("Using compatibility stream…");
        if (!Hls.isSupported()) {
          setStatus("This browser cannot play the compatibility stream.");
          scheduleWhepRetry();
          return;
        }
        hls = new Hls({ enableWorker: true, lowLatencyMode: true, backBufferLength: 0, xhrSetup: xhr => xhr.setRequestHeader("Authorization", `Bearer ${tokenRef.current}`) });
        hls.on(Hls.Events.MANIFEST_PARSED, () => { if (!cancelled) { hlsFatalRetryCount = 0; setStatus("Buffering compatibility stream…"); void video.play().catch(() => undefined); } });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (cancelled || !data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            const retryDelay = Math.min(3_000, 1_000 * 2 ** Math.min(hlsRetryCount, 2));
            hlsRetryCount += 1;
            setStatus("Waiting for live video…");
            window.verseDesktop?.logScreenShareStage({ stage: "hls-network-retry", detail: `retry in ${retryDelay}ms (${data.details})` });
            hls?.destroy();
            hls = null;
            window.clearTimeout(hlsRetryTimer);
            hlsRetryTimer = window.setTimeout(() => {
              hlsRetryTimer = 0;
              startHls();
            }, retryDelay);
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR && Date.now() - mediaRecoveryAt >= 5_000) {
            mediaRecoveryAt = Date.now();
            setStatus("Recovering live video…");
            window.verseDesktop?.logScreenShareStage({ stage: "hls-media-recovery", detail: `${data.details}${data.error?.message ? ` · ${data.error.message}` : ""}` });
            hls?.recoverMediaError();
          } else {
            const retryDelay = Math.min(15_000, 1_500 * 2 ** Math.min(hlsFatalRetryCount, 3));
            hlsFatalRetryCount += 1;
            setStatus("Reconnecting to live video…");
            window.verseDesktop?.logScreenShareStage({ stage: "hls-fatal-retry", detail: `${data.type}/${data.details}${data.error?.message ? ` · ${data.error.message}` : ""}; retry in ${retryDelay}ms` });
            hls?.destroy();
            hls = null;
            window.clearTimeout(hlsRetryTimer);
            hlsRetryTimer = window.setTimeout(() => {
              hlsRetryTimer = 0;
              startHls();
            }, retryDelay);
            scheduleWhepRetry();
          }
        });
        hls.loadSource(media.hlsUrl);
        hls.attachMedia(video);
        waitForVideoFrame();
      } else {
        setStatus("Buffering compatibility stream…");
        waitForVideoFrame();
      }
      scheduleWhepRetry();
    };
    const startWhep = async (probeFromHls = false) => {
      if (!media.whepUrl) { startHls(); return; }
      if (peer || whepStarting || (receivedVideo && !(probeFromHls && video.dataset.liveTransport === "hls"))) return;
      const attemptId = ++whepAttemptId;
      let attemptPeer: RTCPeerConnection | null = null;
      let requestController: AbortController | null = null;
      const isCurrentAttempt = () => !cancelled && attemptId === whepAttemptId && (!attemptPeer || peer === attemptPeer);
      whepStarting = true;
      window.clearTimeout(whepRetryTimer);
      window.verseDesktop?.logScreenShareStage({ stage: "viewer-whep-start", detail: probeFromHls ? "publisher-ready/fallback probe" : "initial/retry" });
      try {
        const rtcConfig = await liveRtcConfiguration();
        if (!isCurrentAttempt()) return;
        remoteStream = new MediaStream();
        attemptPeer = new RTCPeerConnection(rtcConfig);
        peer = attemptPeer;
        attemptPeer.addTransceiver("video", { direction: "recvonly" });
        attemptPeer.addTransceiver("audio", { direction: "recvonly" });
        attemptPeer.ontrack = event => {
          if (!isCurrentAttempt()) return;
          const tracks = event.streams[0]?.getTracks() || [event.track];
          tracks.forEach(track => {
            if (!remoteStream.getTracks().some(existing => existing.id === track.id)) remoteStream.addTrack(track);
          });
          if (event.track.kind === "video") {
            hls?.destroy();
            hls = null;
            receivedVideo = false;
            video.srcObject = remoteStream;
            video.dataset.liveTransport = "whep";
            delete video.dataset.liveLatencyMs;
            setStatus("Receiving video…");
            void video.play().catch(() => undefined);
            waitForVideoFrame();
            event.track.onended = () => { if (isCurrentAttempt()) startHls(); };
          }
        };
        attemptPeer.onconnectionstatechange = () => {
          if (!isCurrentAttempt()) return;
          if (attemptPeer?.connectionState === "failed") startHls();
          if (attemptPeer?.connectionState === "disconnected") {
            window.clearTimeout(disconnectedTimer);
            disconnectedTimer = window.setTimeout(() => {
              if (isCurrentAttempt() && attemptPeer?.connectionState === "disconnected") startHls();
            }, 2500);
          } else {
            window.clearTimeout(disconnectedTimer);
          }
        };
        const offer = await attemptPeer.createOffer();
        await attemptPeer.setLocalDescription(offer);
        if (!isCurrentAttempt()) return;
        const iceGatherStartedAt = performance.now();
        await waitForIceGathering(attemptPeer);
        window.verseDesktop?.logScreenShareStage({ stage: "viewer-ice-gathering", detail: `${Math.round(performance.now() - iceGatherStartedAt)}ms` });
        if (!isCurrentAttempt()) return;
        const authorization = `Basic ${btoa(`versecomms:${tokenRef.current}`)}`;
        const attemptRequestController = new AbortController();
        requestController = attemptRequestController;
        whepRequestController = attemptRequestController;
        const requestTimeout = window.setTimeout(() => attemptRequestController.abort(), 8_000);
        let response: Response;
        try {
          response = await fetch(media.whepUrl, { method: "POST", headers: { Authorization: authorization, "Content-Type": "application/sdp", Accept: "application/sdp" }, body: attemptPeer.localDescription?.sdp || offer.sdp || "", signal: attemptRequestController.signal });
        } finally {
          window.clearTimeout(requestTimeout);
          if (whepRequestController === attemptRequestController) whepRequestController = null;
        }
        if (!isCurrentAttempt()) return;
        if (!response.ok) throw new Error(`WHEP HTTP ${response.status}`);
        const location = response.headers.get("Location");
        if (!location) throw new Error("WHEP session URL missing");
        const sessionUrl = new URL(location, media.whepUrl).toString();
        whepSessionUrl = sessionUrl;
        const answer = await response.text();
        if (!isCurrentAttempt()) {
          void fetch(sessionUrl, { method: "DELETE", headers: { Authorization: authorization } }).catch(() => undefined);
          return;
        }
        await attemptPeer.setRemoteDescription({ type: "answer", sdp: answer });
        if (!isCurrentAttempt()) return;
        fallbackTimer = window.setTimeout(() => { if (!receivedVideo) startHls(); }, 5_000);
      } catch (error) {
        if (!isCurrentAttempt()) return;
        window.verseDesktop?.logScreenShareStage({ stage: "whep-connect-failed", detail: error instanceof Error ? error.message : String(error) });
        const retryForPublisherReady = publisherReadyRef.current && !publisherReadyRetryConsumed;
        startHls();
        if (retryForPublisherReady && !cancelled) {
          publisherReadyRetryConsumed = true;
          window.clearTimeout(whepRetryTimer);
          whepRetryTimer = 0;
          whepRetryCount = 0;
          void startWhep(true);
        }
      } finally {
        if (attemptId === whepAttemptId) whepStarting = false;
      }
    };
    const retryForReadyPublisher = () => {
      if (cancelled || !media.whepUrl || !publisherReadyRef.current) return;
      if (video.dataset.liveTransport !== "hls" && peer) return;
      window.verseDesktop?.logScreenShareStage({ stage: "viewer-publisher-ready", detail: `${video.dataset.liveTransport || "connecting"}; peer=${peer?.connectionState || "none"}` });
      publisherReadyRetryConsumed = true;
      window.clearTimeout(whepRetryTimer);
      whepRetryTimer = 0;
      whepRetryCount = 0;
      if (video.dataset.liveTransport === "hls") {
        setStatus("Publisher is back · restoring low-latency video…");
        void startWhep(true);
      } else if (!peer) {
        setStatus("Publisher is ready · reconnecting with low latency…");
        void startWhep();
      } else {
        publisherReadyRetryConsumed = false;
      }
    };
    retryWhepOnPublisherReadyRef.current = retryForReadyPublisher;
    frameWatchTimer = window.setInterval(() => {
      const now = Date.now();
      if (cancelled || document.visibilityState === "hidden" || !lastDecodedFrameAt || now - lastDecodedFrameAt < 3_000 || now < nextFrameRecoveryAt) return;
      const recoveryDelay = Math.min(6_000, 1_500 * 2 ** Math.min(frameRecoveryCount, 2));
      frameRecoveryCount += 1;
      nextFrameRecoveryAt = now + recoveryDelay;
      receivedVideo = false;
      window.verseDesktop?.logScreenShareStage({ stage: "viewer-frame-stall", detail: `${video.dataset.liveTransport || "unknown"} transport; retrying media path in ${recoveryDelay}ms` });
      if (video.dataset.liveTransport === "whep") {
        startHls();
      } else if (video.dataset.liveTransport === "hls") {
        hls?.destroy();
        hls = null;
        video.removeAttribute("src");
        video.load();
        setStatus("Live video stalled · reconnecting…");
        if (peer) {
          // Let an in-flight WHEP probe finish without competing with a stalled
          // HLS element; a new decoded frame resets the backoff above.
          return;
        }
        void startWhep();
      }
    }, 1_500);
    void startWhep();
    return () => {
      cancelled = true;
      if (retryWhepOnPublisherReadyRef.current === retryForReadyPublisher) retryWhepOnPublisherReadyRef.current = null;
      window.clearTimeout(fallbackTimer);
      window.clearTimeout(disconnectedTimer);
      window.clearTimeout(whepRetryTimer);
      window.clearInterval(frameWatchTimer);
      window.clearTimeout(hlsRetryTimer);
      hls?.destroy();
      stopWhep();
      video.srcObject = null;
      video.removeAttribute("src");
      delete video.dataset.liveTransport;
      delete video.dataset.liveLatencyMs;
      video.load();
    };
  }, [media.whepUrl, media.hlsUrl]);
  return <div className="live-hls-player"><video ref={videoRef} playsInline controls aria-label={`${label} live screen`} /><div className="live-hls-status">{status}</div></div>;
}

async function captureDesktopSource(source: DesktopCaptureSource, profile: QualityProfile, config: ShareConfig): Promise<CaptureSession> {
  const bridge = window.verseDesktop;
  if (!bridge) throw new Error("Desktop capture is unavailable.");
  const sourceKind = source.id.startsWith("screen:") ? "screen" : source.id.startsWith("window:") ? "window" : "";
  if (!sourceKind) throw new Error("Invalid desktop capture source.");
  bridge.logScreenShareStage({ stage: "prepare-display-capture", sourceKind });
  const prepared = await bridge.prepareDisplayCapture(source.id);
  if (!prepared.ok) throw new Error(prepared.error || "Could not prepare screen capture.");
  bridge.logScreenShareStage({ stage: "get-display-media", sourceKind });
  let captured: MediaStream;
  try {
    captured = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: "never" } as unknown as MediaTrackConstraints, audio: config.includeComputerAudio });
  } catch (error) {
    bridge.logScreenShareStage({ stage: "get-display-media-failed", sourceKind, detail: error instanceof Error ? `${error.name}: ${error.message}` : String(error) });
    throw error;
  }
  const track = captured.getVideoTracks()[0];
  if (!track) {
    captured.getTracks().forEach(mediaTrack => mediaTrack.stop());
    throw new Error("Electron returned no screen video track.");
  }
  if (config.includeComputerAudio && !captured.getAudioTracks().length) {
    captured.getTracks().forEach(mediaTrack => mediaTrack.stop());
    throw new Error("Computer audio was not available for this screen share.");
  }
  try { track.contentHint = "motion"; } catch { /* optional */ }
  await track.applyConstraints({ width: { max: profile.width }, height: { max: profile.height }, frameRate: { max: profile.fps } }).catch(error => {
    bridge.logScreenShareStage({ stage: "apply-constraints-warning", sourceKind, detail: error instanceof Error ? error.message : String(error) });
  });
  const sourceStreams = [captured];
  let microphoneStream: MediaStream | undefined;
  // Voice calls own the microphone. Opening a second getUserMedia capture here
  // produces duplicate/echoed input on Chromium and can interrupt the call.
  // Keep the call mic as the single owner and continue screen sharing silently.
  const voiceMicActive = Boolean((window as Window & { __verseVoiceMicActive?: boolean }).__verseVoiceMicActive);
  if (config.includeMicrophone && !voiceMicActive) {
    try {
      microphoneStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      sourceStreams.push(microphoneStream);
    } catch (error) {
      captured.getTracks().forEach(mediaTrack => mediaTrack.stop());
      bridge.logScreenShareStage({ stage: "microphone-capture-failed", detail: error instanceof Error ? `${error.name}: ${error.message}` : String(error) });
      throw new Error("Microphone access was not available. Turn the microphone off or allow access and try again.");
    }
  }
  if (config.includeMicrophone && voiceMicActive) {
    bridge.logScreenShareStage({ stage: "microphone-owned-by-voice-call" });
  }
  let audioContext: AudioContext | undefined;
  let computerGain: GainNode | undefined;
  let microphoneGain: GainNode | undefined;
  const wantsAudio = config.includeComputerAudio || Boolean(microphoneStream);
  const output = new MediaStream([track]);
  if (wantsAudio) {
    audioContext = new AudioContext();
    const destination = audioContext.createMediaStreamDestination();
    if (config.includeComputerAudio) {
      computerGain = audioContext.createGain();
      computerGain.gain.value = config.computerAudioVolume / 100;
      audioContext.createMediaStreamSource(new MediaStream(captured.getAudioTracks())).connect(computerGain).connect(destination);
    }
    if (microphoneStream) {
      microphoneGain = audioContext.createGain();
      microphoneGain.gain.value = config.microphoneVolume / 100;
      audioContext.createMediaStreamSource(microphoneStream).connect(microphoneGain).connect(destination);
    }
    destination.stream.getAudioTracks().forEach(audioTrack => output.addTrack(audioTrack));
    await audioContext.resume().catch(() => undefined);
  }
  const settings = track.getSettings();
  bridge.logScreenShareStage({ stage: "capture-ready", sourceKind, detail: `${settings.width || "?"}x${settings.height || "?"}@${settings.frameRate || "?"}; computer=${config.includeComputerAudio ? config.computerAudioVolume : "off"}; microphone=${config.includeMicrophone ? config.microphoneVolume : "off"}` });
  return { stream: output, sourceStreams, audioContext, computerGain, microphoneGain };
}

function useLiveShare(scope: LiveScope) {
  const { send, subscribe } = useRealtime(); const auth = useAuth(); const [streams, setStreams] = useState<LiveStream[]>([]); const [selectedId, setSelectedId] = useState(""); const [shareError, setShareError] = useState(""); const [sharing, setSharing] = useState(false); const [config, setConfig] = useState<ShareConfig | null>(null); const [watching, setWatching] = useState<WatchResponse | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null); const captureSessionRef = useRef<CaptureSession | null>(null); const localStreamIdRef = useRef(""); const publisherRef = useRef<WhipPublisher | null>(null); const publisherStartingRef = useRef(false); const publisherGenerationRef = useRef(0); const publisherAttemptRef = useRef<AbortController | null>(null); const publisherRetryTimerRef = useRef<number | undefined>(undefined); const publisherRetryCountRef = useRef(0); const qualityRef = useRef<QualityMode>("auto"); const autoIndexRef = useRef(2); const lastAutoAdjustmentRef = useRef(0); const scopeRef = useRef(scope); const watchingRef = useRef<WatchResponse | null>(null);
  useEffect(() => { scopeRef.current = scope; }, [scope]);
  useEffect(() => { watchingRef.current = watching; }, [watching]);
  const stopPublisher = useCallback((notify = true) => { publisherGenerationRef.current += 1; window.clearTimeout(publisherRetryTimerRef.current); publisherRetryTimerRef.current = undefined; publisherAttemptRef.current?.abort(); publisherAttemptRef.current = null; publisherRef.current?.dispose(); publisherRef.current = null; publisherStartingRef.current = false; if (notify && localStreamIdRef.current) send({ type: "live_stream_publisher_idle", streamId: localStreamIdRef.current }); }, [send]);
  const closeViewer = useCallback(() => { const id = watchingRef.current?.stream.id; if (id) void api(`/live/${encodeURIComponent(id)}/leave`, { method: "POST" }).catch(() => undefined); watchingRef.current = null; setWatching(null); }, []);
  const stopSharing = useCallback(() => { const id = localStreamIdRef.current; if (id) send({ type: "live_stream_stop", streamId: id }); stopPublisher(); localStreamRef.current?.getTracks().forEach(track => track.stop()); captureSessionRef.current?.sourceStreams.forEach(stream => stream.getTracks().forEach(track => track.stop())); void captureSessionRef.current?.audioContext?.close().catch(() => undefined); captureSessionRef.current = null; localStreamRef.current = null; localStreamIdRef.current = ""; setSharing(false); setConfig(null); window.dispatchEvent(new CustomEvent("versecomms:live-share-state", { detail: { ...scopeRef.current, sharing: false } })); }, [send, stopPublisher]);
  const applyQuality = useCallback(async (mode: QualityMode, index = autoIndexRef.current) => { const profile = profileFor(mode, index); const track = localStreamRef.current?.getVideoTracks()[0]; if (track) await track.applyConstraints({ width: { max: profile.width }, height: { max: profile.height }, frameRate: { max: profile.fps } }).catch(() => undefined); if (publisherRef.current && track) await configureSender(publisherRef.current.sender, profile, track); }, []);
  const queuePublisherRecovery = useCallback((reason: string) => { if (!localStreamRef.current || !localStreamIdRef.current || publisherRetryTimerRef.current !== undefined) return; const attempt = publisherRetryCountRef.current; const delay = publisherReconnectDelay(attempt); publisherRetryCountRef.current = Math.min(attempt + 1, 5); window.verseDesktop?.logScreenShareStage({ stage: "publisher-reconnect-scheduled", detail: `${reason}; retry ${attempt + 1} in ${delay}ms` }); publisherRetryTimerRef.current = window.setTimeout(() => { publisherRetryTimerRef.current = undefined; const id = localStreamIdRef.current; if (!id || !localStreamRef.current) return; void api<{ media?: MediaGrant | null }>(`/live/${encodeURIComponent(id)}/publish-media`, { method: "POST" }).then(result => startPublisher(result.media || null)).catch(error => { setShareError(error instanceof Error ? error.message : "Live publisher reconnect failed; retrying."); queuePublisherRecovery("grant-refresh-failed"); }); }, delay); }, []);
  const startPublisher = useCallback(async (media: MediaGrant | null) => {
    if (!media || !localStreamRef.current || publisherRef.current || publisherStartingRef.current) return;
    publisherStartingRef.current = true;
    if (qualityRef.current === "auto") lastAutoAdjustmentRef.current = Date.now();
    const generation = publisherGenerationRef.current;
    const attemptController = new AbortController();
    publisherAttemptRef.current = attemptController;
    let createdPublisher: WhipPublisher | null = null;
    let earlyFailure: string | null = null;
    try {
      const profile = profileFor(qualityRef.current, autoIndexRef.current);
      await applyQuality(qualityRef.current, autoIndexRef.current);
      const capturedStream = localStreamRef.current;
      if (!capturedStream) return;
      const publisher = await publishWhip(media, capturedStream, profile, (available, lossRatio, encoderLimited) => {
        if (qualityRef.current !== "auto") return;
        const now = Date.now();
        const nextIndex = nextAutoQualityIndex(AUTO_LADDER, autoIndexRef.current, available, lossRatio, now - lastAutoAdjustmentRef.current, encoderLimited);
        if (nextIndex === autoIndexRef.current) return;
        autoIndexRef.current = nextIndex;
        lastAutoAdjustmentRef.current = now;
        window.verseDesktop?.logScreenShareStage({ stage: "auto-quality-change", detail: `${profileFor("auto", nextIndex).label}; available=${Math.round(available)}bps; loss=${(lossRatio * 100).toFixed(1)}%` });
        void applyQuality("auto", autoIndexRef.current).catch(() => undefined);
      }, state => {
        if (state === "connected" || state === "completed") {
          publisherRetryCountRef.current = 0;
          if (qualityRef.current === "auto") lastAutoAdjustmentRef.current = Date.now();
          setShareError("");
        } else if (["failed", "disconnected", "closed"].includes(state)) {
          if (createdPublisher) {
            createdPublisher.dispose();
            if (publisherRef.current === createdPublisher) publisherRef.current = null;
          } else {
            earlyFailure = state;
          }
          queuePublisherRecovery(state);
        }
      }, attemptController.signal);
      createdPublisher = publisher;
      if (generation !== publisherGenerationRef.current || !localStreamRef.current) {
        publisher.dispose();
        return;
      }
      if (earlyFailure) {
        publisher.dispose();
        return;
      }
      publisherRef.current = publisher;
      send({ type: "live_stream_publisher_ready", streamId: localStreamIdRef.current });
    } catch (error) {
      if (!attemptController.signal.aborted) {
        setShareError(error instanceof Error ? error.message : "Live streaming is temporarily unavailable.");
        queuePublisherRecovery("publish-failed");
      }
    } finally {
      if (publisherAttemptRef.current === attemptController) publisherAttemptRef.current = null;
      if (generation === publisherGenerationRef.current) publisherStartingRef.current = false;
    }
  }, [applyQuality, queuePublisherRecovery, send]);
  useEffect(() => { if (!scope.scopeId) return; void api<{ streams?: LiveStream[] }>(`/live/${scope.scopeType}/${encodeURIComponent(scope.scopeId)}`).then(result => setStreams(Array.isArray(result.streams) ? result.streams : [])).catch(() => undefined); send({ type: "live_stream_list", scopeType: scope.scopeType, scopeId: scope.scopeId }); return subscribe((payload: RealtimePayload) => { if (payload.scopeType !== scope.scopeType || payload.scopeId !== scope.scopeId) return; if (payload.type === "live_stream_state") { const next = Array.isArray(payload.streams) ? payload.streams as LiveStream[] : []; console.info("[VIEWER_RECEIVED_STREAM_LIST]", { scopeType: scope.scopeType, scopeId: scope.scopeId, streamCount: next.length }); setStreams(next); } if (payload.type === "live_stream_started" && payload.stream) setStreams(current => current.some(item => item.id === (payload.stream as LiveStream).id) ? current : [...current, payload.stream as LiveStream]); if (payload.type === "live_stream_stopped" && payload.streamId) { if (String(payload.streamId) === localStreamIdRef.current) stopSharing(); if (String(payload.streamId) === watchingRef.current?.stream.id) closeViewer(); setStreams(current => current.filter(item => item.id !== String(payload.streamId))); } if (payload.type === "live_stream_publisher_needed" && String(payload.streamId) === localStreamIdRef.current) void startPublisher((payload.media || null) as MediaGrant | null); if (payload.type === "live_stream_publisher_idle" && String(payload.streamId) === localStreamIdRef.current) stopPublisher(); }); }, [closeViewer, send, scope.scopeId, scope.scopeType, startPublisher, stopPublisher, stopSharing, subscribe]);
  useEffect(() => () => { closeViewer(); stopSharing(); }, [closeViewer, stopSharing]);
  useEffect(() => {
    if (!watching?.stream.id) return;
    const streamId = watching.stream.id;
    let active = true;
    let hidden = document.hidden;
    let heartbeatTask: Promise<void> | null = null;
    let leaveRequest: Promise<unknown> | null = null;
    const heartbeat = (): Promise<void> => {
      if (!active || hidden) return Promise.resolve();
      if (heartbeatTask) return heartbeatTask;
      const task = (async () => {
        try {
          const media = watchingRef.current?.media;
          const expiresAt = Date.parse(media?.expiresAt || "");
          const refresh = !Number.isFinite(expiresAt) || expiresAt - Date.now() < 60_000;
          const result = await api<{ media?: MediaGrant | null }>(`/live/${encodeURIComponent(streamId)}/heartbeat`, {
            method: "POST",
            body: JSON.stringify({ mediaExpiresAt: media?.expiresAt || "" }),
          });
          if (!active) return;
          if (result.media) {
            setWatching(current => current?.stream.id === streamId ? { ...current, media: result.media || current.media } : current);
          } else if (refresh) {
            const mediaResult = await api<{ media?: MediaGrant | null }>(`/live/${encodeURIComponent(streamId)}/media`, { method: "POST" });
            if (active && mediaResult.media) {
              setWatching(current => current?.stream.id === streamId ? { ...current, media: mediaResult.media || current.media } : current);
            }
          }
        } catch {
          // Retain the current stream and retry on the next interval or visibility return.
        }
      })().finally(() => { heartbeatTask = null; });
      heartbeatTask = task;
      return task;
    };
    const timer = window.setInterval(() => { void heartbeat(); }, 15_000);
    const onVisibility = () => {
      hidden = document.hidden;
      if (hidden) {
        leaveRequest = (heartbeatTask || Promise.resolve())
          .then(() => api(`/live/${encodeURIComponent(streamId)}/leave`, { method: "POST" }))
          .catch(() => undefined);
      } else {
        // Wait for any hidden-state leave to settle before restoring presence;
        // otherwise a slow leave response could race and remove the viewer again.
        void Promise.resolve(leaveRequest).then(() => heartbeat());
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [watching?.stream.id]);
  const openShareScreen = useCallback(() => { if (!isDesktopApp() || sharing) return; setShareError(""); window.verseDesktop?.showSharePicker(); }, [sharing]);
  useEffect(() => { const onOpen = (event: Event) => { const detail = (event as CustomEvent<{ scopeType?: string; scopeId?: string }>).detail; if (detail?.scopeType === scope.scopeType && detail.scopeId === scope.scopeId) void openShareScreen(); }; window.addEventListener("versecomms:open-live-share", onOpen); return () => window.removeEventListener("versecomms:open-live-share", onOpen); }, [openShareScreen, scope.scopeId, scope.scopeType]);
  const startSharing = useCallback(async (requestedConfig?: ShareConfig) => { const nextConfig = requestedConfig || config; if (!nextConfig || !isDesktopApp() || sharing) return; setShareError(""); try { const profile = profileFor(nextConfig.quality, 2); const capture = await captureDesktopSource(nextConfig.source, profile, nextConfig); const captured = capture.stream; const track = captured.getVideoTracks()[0]; if (track) { try { track.contentHint = "motion"; } catch { /* optional */ } await track.applyConstraints({ width: { max: profile.width }, height: { max: profile.height }, frameRate: { max: profile.fps } }).catch(() => undefined); track.onended = () => { window.verseDesktop?.logScreenShareStage({ stage: "capture-track-ended" }); stopSharing(); }; } const id = streamId(); captureSessionRef.current = capture; localStreamIdRef.current = id; localStreamRef.current = captured; qualityRef.current = nextConfig.quality; autoIndexRef.current = 2; publisherRetryCountRef.current = 0; setConfig(nextConfig); setSharing(true); window.verseDesktop?.logScreenShareStage({ stage: "STREAM_START_REQUEST", detail: `${scope.scopeType}:${scope.scopeId}; computer=${nextConfig.includeComputerAudio ? nextConfig.computerAudioVolume : "off"}; microphone=${nextConfig.includeMicrophone ? nextConfig.microphoneVolume : "off"}` }); send({ type: "live_stream_start", streamId: id, scopeType: scope.scopeType, scopeId: scope.scopeId, sourceLabel: safeSourceLabel(nextConfig.source), quality: nextConfig.quality, hasAudio: nextConfig.includeComputerAudio || nextConfig.includeMicrophone, platform: "electron" }); window.dispatchEvent(new CustomEvent("versecomms:live-share-state", { detail: { ...scope, sharing: true } })); } catch (error) { setShareError(error instanceof Error ? error.message : "Screen sharing could not start."); localStreamRef.current?.getTracks().forEach(track => track.stop()); captureSessionRef.current?.sourceStreams.forEach(stream => stream.getTracks().forEach(track => track.stop())); void captureSessionRef.current?.audioContext?.close().catch(() => undefined); captureSessionRef.current = null; localStreamRef.current = null; } }, [config, scope.scopeId, scope.scopeType, send, sharing, stopSharing]);
  useEffect(() => window.verseDesktop?.onSharePickerAction(selection => { void startSharing({ source: selection.source, quality: selection.quality, includeComputerAudio: selection.includeComputerAudio, computerAudioVolume: selection.computerAudioVolume, includeMicrophone: selection.includeMicrophone, microphoneVolume: selection.microphoneVolume }); }), [startSharing]);
  const setAudioVolume = useCallback((kind: "computer" | "microphone", value: number) => { const volume = Math.max(0, Math.min(100, Math.round(value))); const gain = kind === "computer" ? captureSessionRef.current?.computerGain : captureSessionRef.current?.microphoneGain; if (!gain || !captureSessionRef.current?.audioContext) return; gain.gain.setTargetAtTime(volume / 100, captureSessionRef.current.audioContext.currentTime, .015); setConfig(current => current ? { ...current, ...(kind === "computer" ? { computerAudioVolume: volume } : { microphoneVolume: volume }) } : current); }, []);
  const watchStream = useCallback(async (stream: LiveStream) => { if (stream.ownerId === auth.user?.id || watching?.stream.id === stream.id) return; closeViewer(); setShareError(""); try { setWatching(await api<WatchResponse>(`/live/${encodeURIComponent(stream.id)}/watch`, { method: "POST" })); } catch (error) { setShareError(error instanceof Error ? error.message : "Live streaming is temporarily unavailable."); } }, [auth.user?.id, closeViewer, watching?.stream.id]);
  return { streams, selectedId, setSelectedId, sharing, shareError, config, openShareScreen, stopSharing, setAudioVolume, watchStream, closeViewer, watching, localStreamRef, currentUserId: auth.user?.id || "", qualityLabel: qualityRef.current === "auto" ? `Auto · ${profileFor("auto", autoIndexRef.current).label}` : qualityRef.current };
}

export function ConversationLive({ scopeType, scopeId, label }: LiveScope) {
  const live = useLiveShare({ scopeType, scopeId, label }); const desktop = isDesktopApp(); const [liveOpen, setLiveOpen] = useState(false); const [fallbackStream, setFallbackStream] = useState<LiveStream | null>(null);
  const controllerShown = useRef(false);
  useEffect(() => {
    if (!desktop) return;
    window.verseDesktop?.setScreenShareActive(live.sharing);
    if (!live.sharing) { window.verseDesktop?.hideShareController(); controllerShown.current = false; return; }
    const own = live.streams.find(stream => stream.ownerId === live.currentUserId);
    const state = { sourceLabel: live.config ? safeSourceLabel(live.config.source) : "Screen", qualityLabel: live.qualityLabel, audioLabel: shareAudioLabel(live.config),
      computerAudioAvailable: Boolean(live.config?.includeComputerAudio), computerAudioEnabled: Boolean(live.config?.includeComputerAudio && live.config.computerAudioVolume > 0), computerAudioVolume: live.config?.computerAudioVolume || 0,
      microphoneAvailable: Boolean(live.config?.includeMicrophone), microphoneEnabled: Boolean(live.config?.includeMicrophone && live.config.microphoneVolume > 0), microphoneVolume: live.config?.microphoneVolume || 0,
      viewerCount: own?.viewerCount || 0, statusLabel: live.shareError ? "Needs attention" : own?.viewerCount ? "Live" : "Waiting for viewers" };
    if (!controllerShown.current) { window.verseDesktop?.showShareController(state); controllerShown.current = true; }
    else window.verseDesktop?.updateShareController(state);
  }, [desktop, live.sharing, live.streams, live.currentUserId, live.config, live.qualityLabel, live.shareError]);
  useEffect(() => {
    if (!desktop) return;
    return window.verseDesktop?.onLiveControllerAction(command => {
      if (!live.sharing) return;
      if (command.action === "stop") live.stopSharing();
      if (command.action === "change-source") { live.stopSharing(); window.verseDesktop?.showSharePicker(); }
      if (command.action === "show-controls") window.verseDesktop?.showShareController();
      if (command.action === "computer-volume") live.setAudioVolume("computer", command.value || 0);
      if (command.action === "microphone-volume") live.setAudioVolume("microphone", command.value || 0);
    });
  }, [desktop, live.sharing, live.stopSharing, live.setAudioVolume]);
  useEffect(() => {
    if (!liveOpen) return;
    const key = (event: KeyboardEvent) => { if (event.key === "Escape") setLiveOpen(false); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [liveOpen]);
  const watch = (stream: LiveStream) => {
    const chatRoute = window.location.pathname;
    if (desktop) window.verseDesktop?.openLiveViewer({ streamId: stream.id, chatRoute });
    else {
      const query = new URLSearchParams({ desktopWindow: "live-viewer", streamId: stream.id, chatRoute });
      const popup = window.open("/home?" + query, "versecomms-live-viewer", "popup=yes,width=960,height=600,resizable=yes");
      if (popup) popup.focus(); else setFallbackStream(stream);
    }
    setLiveOpen(false);
  };
  const visibleStreams = live.streams;
  return <><button className={`live-button ${live.streams.length || live.sharing ? "active" : ""}`} type="button" onClick={() => setLiveOpen(current => !current)} title="Open LIVE broadcasts" aria-expanded={liveOpen}><span className={`live-button-dot ${live.streams.length || live.sharing ? "active" : ""}`} /><Radio size={14} /> LIVE{live.streams.length > 1 ? <b>{live.streams.length}</b> : null}</button>
    {liveOpen ? <LiveStackDeck streams={visibleStreams} previewStream={live.localStreamRef.current} previewOwnerId={live.currentUserId} onWatch={watch} onClose={() => setLiveOpen(false)} /> : null}
    {fallbackStream ? createPortal(<div className="live-browser-fallback"><DetachedLiveViewerWindow streamId={fallbackStream.id} chatRoute={window.location.pathname} onClose={() => setFallbackStream(null)} /></div>, document.body) : null}
    {live.shareError && !liveOpen ? <div className="live-error">{live.shareError}</div> : null}</>;
}

function formatLiveDuration(startedAt: string) { const seconds = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)); return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`; }

export function ShareScreenButton({ scopeType, scopeId }: { scopeType: "dm" | "channel"; scopeId: string }) {
  const [sharing, setSharing] = useState(false);
  useEffect(() => { const onState = (event: Event) => { const detail = (event as CustomEvent<{ scopeType?: string; scopeId?: string; sharing?: boolean }>).detail; if (detail?.scopeType === scopeType && detail.scopeId === scopeId) setSharing(Boolean(detail.sharing)); }; window.addEventListener("versecomms:live-share-state", onState); return () => window.removeEventListener("versecomms:live-share-state", onState); }, [scopeId, scopeType]);
  if (!isDesktopApp()) return null;
  return <IconButton className={`share-screen-button ${sharing ? "active" : ""}`} label={sharing ? "Manage live screen share" : "Share screen"} disabled={!scopeId} onClick={() => { if (sharing) window.verseDesktop?.showShareController(); else window.dispatchEvent(new CustomEvent("versecomms:open-live-share", { detail: { scopeType, scopeId } })); }}><ScreenShare size={18} />{sharing ? <span className="share-screen-live-label">LIVE</span> : null}</IconButton>;
}

type LiveStackDeckProps = {
  streams: LiveStream[];
  previewStream?: MediaStream | null;
  previewOwnerId?: string;
  onWatch: (stream: LiveStream) => void;
  onClose: () => void;
};

function LocalLivePreview({ stream }: { stream: MediaStream }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    void video.play().catch(() => undefined);
    return () => {
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [stream]);
  return <div className="live-stack-preview"><video ref={videoRef} autoPlay muted playsInline aria-label="Your screen share preview" /><span>Preview</span></div>;
}

export function LiveStackDeck({
  streams,
  previewStream,
  previewOwnerId,
  onWatch,
  onClose
}: LiveStackDeckProps) {
  const [index, setIndex] = useState(0);
  const [remotePreview, setRemotePreview] = useState<MediaGrant | null>(null);
  const overlayRef = useRef<HTMLElement>(null);
  useEffect(() => { overlayRef.current?.focus(); }, []);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    currentX: number;
  } | null>(null);

  useEffect(() => {
    if (!streams.length) {
      setIndex(0);
      return;
    }

    if (index >= streams.length) {
      setIndex(streams.length - 1);
    }
  }, [streams.length, index]);

  const move = useCallback((direction: -1 | 1) => {
    if (streams.length <= 1) return;

    setIndex(current =>
      (current + direction + streams.length) % streams.length
    );
  }, [streams.length]);

  function relativePosition(cardIndex: number) {
    if (!streams.length) return "far";

    let difference =
      (cardIndex - index + streams.length) % streams.length;

    if (difference > streams.length / 2) {
      difference -= streams.length;
    }

    if (difference === 0) return "front";
    if (difference === 1) return "next";
    if (difference === -1) return "previous";
    if (difference === 2) return "back";

    return "far";
  }

  function onPointerDown(
    event: React.PointerEvent<HTMLElement>,
    cardIndex: number
  ) {
    if (cardIndex !== index || event.button !== 0 || (event.target as HTMLElement).closest("button")) return;

    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      currentX: event.clientX
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(
    event: React.PointerEvent<HTMLElement>,
    cardIndex: number
  ) {
    const drag = dragRef.current;

    if (
      !drag ||
      cardIndex !== index ||
      drag.pointerId !== event.pointerId
    ) {
      return;
    }

    drag.currentX = event.clientX;

    const delta = drag.currentX - drag.startX;
    const card = event.currentTarget;

    card.style.setProperty("--drag-x", `${delta}px`);
    card.style.setProperty("--drag-r", `${delta / 24}deg`);
  }

  function onPointerUp(
    event: React.PointerEvent<HTMLElement>,
    cardIndex: number
  ) {
    const drag = dragRef.current;

    if (
      !drag ||
      cardIndex !== index ||
      drag.pointerId !== event.pointerId
    ) {
      return;
    }

    const delta = drag.currentX - drag.startX;

    dragRef.current = null;

    event.currentTarget.style.removeProperty("--drag-x");
    event.currentTarget.style.removeProperty("--drag-r");

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (event.type === "pointercancel" || Math.abs(delta) < 70) return;

    move(delta < 0 ? 1 : -1);
  }

  const selected = streams[Math.min(index, streams.length - 1)];

  useEffect(() => {
    if (!selected || selected.ownerId === previewOwnerId) {
      setRemotePreview(null);
      return;
    }
    let cancelled = false;
    void api<WatchResponse>(`/live/${encodeURIComponent(selected.id)}/watch`, { method: "POST" })
      .then(result => { if (!cancelled) setRemotePreview(result.media); })
      .catch(() => { if (!cancelled) setRemotePreview(null); });
    return () => {
      cancelled = true;
      void api(`/live/${encodeURIComponent(selected.id)}/leave`, { method: "POST" }).catch(() => undefined);
    };
  }, [previewOwnerId, selected?.id]);

  return (
    <section
      ref={overlayRef}
      className="live-stack-overlay"
      aria-label="Live broadcasts"
      onKeyDown={event => {
        if (event.key === "ArrowLeft") move(-1);
        if (event.key === "ArrowRight") move(1);
        if (event.key === "Escape") onClose();
      }}
      tabIndex={0}
    >
      <header className="live-stack-header">
        <span>
          <i className="live-status-dot" />
          LIVE
        </span>

        <span className="live-stack-count">
          {streams.length}
          {" "}
          {streams.length === 1 ? "broadcast" : "broadcasts"}
        </span>

        <button
          type="button"
          className="live-stack-close"
          onClick={onClose}
          aria-label="Close live broadcasts"
        >
          <X size={18} />
        </button>
      </header>

      {!streams.length ? <div className="live-stack-empty"><Radio size={22} /><strong>Nobody is live</strong><span>Broadcasts appear here when someone starts sharing.</span></div> : null}
      <div className="live-stack-stage" hidden={!streams.length}>
        {streams.map((stream, cardIndex) => {
          const position = relativePosition(cardIndex);

          return (
            <article
              key={stream.id}
              className="live-stack-stream-card"
              data-position={position}
              aria-hidden={cardIndex !== index}
              onPointerDown={event =>
                onPointerDown(event, cardIndex)
              }
              onPointerMove={event =>
                onPointerMove(event, cardIndex)
              }
              onPointerUp={event =>
                onPointerUp(event, cardIndex)
              }
              onPointerCancel={event =>
                onPointerUp(event, cardIndex)
              }
            >
              <div className="live-stack-stream-head">
                <div className="live-stack-avatar">
                  {stream.ownerName
                    .slice(0, 2)
                    .toUpperCase()}
                </div>

                <div className="live-stack-identity">
                  <strong>
                    {stream.ownerName}
                  </strong>

                  <span>
                    {stream.sourceLabel}
                  </span>
                </div>

                <span className="live-stack-badge">
                  <i className="live-status-dot" />
                  LIVE
                </span>
              </div>

              <div className="live-stack-stream-body">
                {previewStream && stream.ownerId === previewOwnerId ? <LocalLivePreview stream={previewStream} /> : cardIndex === index && remotePreview ? <div className="live-stack-preview"><HlsVideo media={remotePreview} label={`${stream.ownerName} live screen`} publisherReady={stream.mediaReady === true} /></div> : <><ScreenShare size={42} /><strong>{stream.sourceLabel}</strong><span>Screen broadcast</span><small>Loading live preview…</small></>}
              </div>

              <footer className="live-stack-stream-footer">
                <div>
                  <span>
                    {stream.viewerCount || 0}
                    {" "}
                    {(stream.viewerCount || 0) === 1
                      ? "viewer"
                      : "viewers"}
                  </span>

                  <span>
                    {formatLiveDuration(stream.startedAt)}
                  </span>
                </div>

                {stream.ownerId === previewOwnerId ? <span className="live-stack-self-label">Your preview</span> : cardIndex === index ? (
                  <button
                    type="button"
                    className="live-stack-watch"
                    onClick={() => selected && onWatch(selected)}
                  >
                    Watch
                  </button>
                ) : null}
              </footer>
            </article>
          );
        })}
      </div>

      <div className="live-stack-navigation">
        <button
          type="button"
          onClick={() => move(-1)}
          aria-label="Previous stream"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="live-stack-dots">
          {streams.map((stream, dotIndex) => (
            <i
              key={stream.id}
              className={
                dotIndex === index
                  ? "active"
                  : ""
              }
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => move(1)}
          aria-label="Next stream"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <p className="live-stack-help">
        Swipe or use the arrows to browse broadcasts.
      </p>
    </section>
  );
}
